/**
 * EscapeResultsToolbar — appears ONLY after a /escape search has run.
 *
 * Founder ruling 18 Feb 2026 PM-9 (after Phase-4 ship):
 *   "Members need fast control to narrow results without opening the
 *    full Refine drawer. Toolbar is post-search only; landing stays
 *    clean and centred. Quick chips elegant, horizontally scrollable
 *    — useful, not crowded."
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ 24 escapes for "Paris"                  [Sort]  [Refine ●]   │  Row 1
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ ‹ Tours · Attractions · Day Trips · Food & Drink · Family · › │  Row 2 (chips, scroll-x)
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ [Paris ✕]  [Tours ✕]  [★ 4.5+ ✕]  [Free cancellation ✕]      │  Row 3 (active filters)
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Quick chips map to Viator taxonomy underneath but render as member-
 * friendly Concierge® labels. Tapping a chip:
 *   - "Skip-the-line"  → toggles flag SKIP_THE_LINE
 *   - "Private Tours"  → toggles flag PRIVATE_TOUR
 *   - everything else  → appended to the searchTerm (Viator free-text)
 *
 * The toolbar is dumb — all state lives in /escape/Escape.jsx. The
 * toolbar receives current values + change handlers.
 */
import React, { useState, useRef, useEffect } from "react";

/* ── Quick chips · founder-curated short list (broad set, scrolls)
 *    Maps "label" → "kind: flag|term" + value sent to backend.
 *    Doctrine: never invent the underlying filter — only chip labels
 *    that genuinely move Viator results.                                */
export const QUICK_CHIPS = [
    { label: "Tours",          kind: "term", value: "tours" },
    { label: "Attractions",    kind: "term", value: "attractions" },
    { label: "Day Trips",      kind: "term", value: "day trips" },
    { label: "Food & Drink",   kind: "term", value: "food and drink" },
    { label: "Family",         kind: "term", value: "family" },
    { label: "Adventure",      kind: "term", value: "adventure" },
    { label: "Shows",          kind: "term", value: "shows" },
    { label: "Private Tours",  kind: "flag", value: "PRIVATE_TOUR" },
    { label: "Skip-the-line",  kind: "flag", value: "SKIP_THE_LINE" },
    { label: "Wellness",       kind: "term", value: "wellness" },
];

/* ── Sort options · founder spec
 *    "Recommended" = DEFAULT (Viator's provider relevance order).
 *    "Recently added" = DATE_ADDED (Viator's new-product sort, added
 *    19 Feb 2026 PM Wave B-2 widening).                                   */
export const SORT_OPTIONS = [
    { id: "RECOMMENDED",       label: "Recommended",          sort: "DEFAULT",          order: "DESCENDING" },
    { id: "RECENTLY_ADDED",    label: "Recently added",       sort: "DATE_ADDED",       order: "DESCENDING" },
    { id: "RATING_DESC",       label: "Traveller rating",     sort: "TRAVELER_RATING",  order: "DESCENDING" },
    { id: "PRICE_ASC",         label: "Price · low to high",  sort: "PRICE",            order: "ASCENDING"  },
    { id: "PRICE_DESC",        label: "Price · high to low",  sort: "PRICE",            order: "DESCENDING" },
    { id: "DURATION_ASC",      label: "Duration · short to long", sort: "DURATION",      order: "ASCENDING"  },
    { id: "DURATION_DESC",     label: "Duration · long to short", sort: "DURATION",      order: "DESCENDING" },
];

const TOOLBAR_INK = "var(--t-ink, #211C15)";
const TOOLBAR_INK2 = "var(--t-ink2, #5C5447)";
const TOOLBAR_INK3 = "var(--t-ink3, #8A8071)";
const TOOLBAR_LINE2 = "var(--t-line2, #D8CDB7)";
const TOOLBAR_CARD = "var(--t-card, #FCFAF4)";
const TOOLBAR_BRASS = "var(--t-brass, #8B6914)";

/* ── Sort dropdown ─────────────────────────────────────────────── */
function SortMenu({ sortId, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        function close(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);
    const current = SORT_OPTIONS.find((s) => s.id === sortId) || SORT_OPTIONS[0];
    return (
        <div ref={ref} style={{ position: "relative" }}>
            <button
                type="button"
                data-testid="escape-toolbar-sort-trigger"
                onClick={() => setOpen((v) => !v)}
                style={{
                    background: TOOLBAR_CARD,
                    color: TOOLBAR_INK,
                    border: `1px solid ${TOOLBAR_LINE2}`,
                    borderRadius: 999,
                    padding: "8px 16px",
                    fontSize: 13,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                }}
            >
                <span style={{ color: TOOLBAR_INK3, fontSize: 11 }}>Sort</span>
                <span style={{ fontWeight: 500 }}>{current.label}</span>
                <span aria-hidden="true" style={{ fontSize: 10, color: TOOLBAR_INK3 }}>▾</span>
            </button>
            {open && (
                <div
                    data-testid="escape-toolbar-sort-menu"
                    role="menu"
                    style={{
                        position: "absolute", top: "calc(100% + 6px)", right: 0,
                        background: TOOLBAR_CARD,
                        border: `1px solid ${TOOLBAR_LINE2}`,
                        borderRadius: 12,
                        minWidth: 220,
                        padding: 6,
                        boxShadow: "0 12px 32px rgba(33,28,21,0.12)",
                        zIndex: 30,
                    }}
                >
                    {SORT_OPTIONS.map((s) => {
                        const isActive = s.id === current.id;
                        return (
                            <button
                                key={s.id}
                                type="button"
                                role="menuitem"
                                data-testid={`escape-toolbar-sort-${s.id.toLowerCase().replace(/_/g, "-")}`}
                                onClick={() => { onChange?.(s); setOpen(false); }}
                                style={{
                                    width: "100%",
                                    textAlign: "left",
                                    padding: "9px 12px",
                                    borderRadius: 8,
                                    background: isActive ? "var(--t-paper, #F4EFE6)" : "transparent",
                                    color: TOOLBAR_INK,
                                    border: 0,
                                    fontSize: 13.5,
                                    cursor: "pointer",
                                    fontWeight: isActive ? 600 : 400,
                                }}
                            >
                                {s.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* ── Quick chip row · horizontally scrollable, faint scroll affordance */
function QuickChipsRow({ active, onToggle }) {
    return (
        <div
            data-testid="escape-toolbar-quick-chips"
            style={{
                display: "flex",
                gap: 8,
                overflowX: "auto",
                paddingBottom: 6,
                scrollbarWidth: "thin",
                WebkitOverflowScrolling: "touch",
            }}
        >
            {QUICK_CHIPS.map((c) => {
                const isActive = active.includes(c.label);
                return (
                    <button
                        key={c.label}
                        type="button"
                        data-testid={`escape-quick-chip-${c.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                        onClick={() => onToggle?.(c)}
                        style={{
                            background: isActive ? TOOLBAR_INK : TOOLBAR_CARD,
                            color: isActive ? TOOLBAR_CARD : TOOLBAR_INK2,
                            border: `1px solid ${isActive ? TOOLBAR_INK : TOOLBAR_LINE2}`,
                            borderRadius: 999,
                            padding: "7px 14px",
                            fontSize: 13,
                            fontFamily: "inherit",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            flex: "0 0 auto",
                            transition: "background 150ms ease",
                        }}
                    >
                        {c.label}
                    </button>
                );
            })}
        </div>
    );
}

/* ── Removable filter chip ─────────────────────────────────────── */
function FilterChip({ label, onRemove, testid }) {
    return (
        <span
            data-testid={testid}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "var(--t-brass-wash, #EFE6D3)",
                color: "var(--t-brass-deep, #7d5b21)",
                border: `1px solid var(--t-brass, ${TOOLBAR_BRASS})`,
                borderRadius: 999,
                padding: "4px 4px 4px 12px",
                fontSize: 12,
                fontWeight: 500,
            }}
        >
            {label}
            <button
                type="button"
                onClick={onRemove}
                data-testid={`${testid}-remove`}
                aria-label={`Remove ${label}`}
                style={{
                    background: "transparent",
                    color: "var(--t-brass-deep, #7d5b21)",
                    border: 0,
                    fontSize: 16,
                    lineHeight: 1,
                    width: 22, height: 22, borderRadius: 999,
                    cursor: "pointer",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}
            >×</button>
        </span>
    );
}

/* ── Toolbar ───────────────────────────────────────────────────── */
export default function EscapeResultsToolbar({
    resultCount,
    queryLabel,
    sortId,
    onSortChange,
    onRefineOpen,
    refineActiveCount = 0,
    activeQuickChips,        // array of chip-label strings
    onToggleQuickChip,
    activeFilterChips,       // [{ key, label, onRemove }, …]
    onClearAllFilters,
    loading = false,
}) {
    const hasActiveFilters = (activeFilterChips || []).length > 0
        || (activeQuickChips || []).length > 0;

    return (
        <section
            data-testid="escape-results-toolbar"
            style={{
                background: TOOLBAR_CARD,
                border: `1px solid var(--t-line, #E5DCCB)`,
                borderRadius: 14,
                padding: "14px 18px",
                marginBottom: 20,
                display: "flex",
                flexDirection: "column",
                gap: 12,
            }}
        >
            {/* Row 1 · count + sort + refine */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                }}
            >
                <div
                    data-testid="escape-toolbar-count"
                    style={{
                        fontSize: 12,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        color: TOOLBAR_INK3,
                        fontFamily: "ui-monospace, monospace",
                    }}
                >
                    {loading
                        ? "Concierge® is curating"
                        : `${resultCount} ${resultCount === 1 ? "escape" : "escapes"}`}
                    {queryLabel && (
                        <>
                            {" "}
                            <span
                                style={{
                                    textTransform: "none",
                                    letterSpacing: 0,
                                    color: TOOLBAR_INK,
                                    fontStyle: "italic",
                                    fontFamily: "inherit",
                                }}
                            >
                                · for &ldquo;{queryLabel}&rdquo;
                            </span>
                        </>
                    )}
                </div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                    <SortMenu sortId={sortId} onChange={onSortChange} />
                    <button
                        type="button"
                        data-testid="escape-toolbar-refine"
                        onClick={onRefineOpen}
                        style={{
                            background: refineActiveCount > 0 ? TOOLBAR_INK : "transparent",
                            color: refineActiveCount > 0 ? TOOLBAR_CARD : TOOLBAR_INK,
                            border: `1px solid ${TOOLBAR_INK}`,
                            borderRadius: 999,
                            padding: "8px 16px",
                            fontSize: 13,
                            fontFamily: "inherit",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                        }}
                    >
                        <span aria-hidden="true">⇅</span>
                        Refine
                        {refineActiveCount > 0 && (
                            <span
                                data-testid="escape-toolbar-refine-badge"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: refineActiveCount > 0 ? TOOLBAR_CARD : TOOLBAR_INK,
                                    color: refineActiveCount > 0 ? TOOLBAR_INK : TOOLBAR_CARD,
                                    minWidth: 18, height: 18,
                                    borderRadius: 999,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    padding: "0 5px",
                                }}
                            >{refineActiveCount}</span>
                        )}
                    </button>
                </div>
            </div>

            {/* Row 2 · quick chip carousel */}
            <QuickChipsRow active={activeQuickChips || []} onToggle={onToggleQuickChip} />

            {/* Row 3 · active filter chips */}
            {hasActiveFilters && (
                <div
                    data-testid="escape-toolbar-active-filters"
                    style={{
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap",
                        alignItems: "center",
                        paddingTop: 4,
                        borderTop: `1px dashed var(--t-line, #E5DCCB)`,
                    }}
                >
                    <span
                        className="t-mono uppercase"
                        style={{
                            fontSize: 10,
                            letterSpacing: "0.18em",
                            color: TOOLBAR_INK3,
                            fontWeight: 600,
                            paddingRight: 4,
                        }}
                    >
                        Active
                    </span>
                    {(activeQuickChips || []).map((label) => (
                        <FilterChip
                            key={`qc-${label}`}
                            label={label}
                            testid={`escape-active-chip-quick-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                            onRemove={() => {
                                const found = QUICK_CHIPS.find((c) => c.label === label);
                                if (found) onToggleQuickChip?.(found);
                            }}
                        />
                    ))}
                    {(activeFilterChips || []).map((f) => (
                        <FilterChip
                            key={`af-${f.key}`}
                            label={f.label}
                            testid={`escape-active-chip-${f.key}`}
                            onRemove={f.onRemove}
                        />
                    ))}
                    {hasActiveFilters && (
                        <button
                            type="button"
                            data-testid="escape-toolbar-clear-all"
                            onClick={onClearAllFilters}
                            style={{
                                background: "transparent",
                                border: 0,
                                color: TOOLBAR_INK3,
                                fontSize: 12,
                                textDecoration: "underline",
                                cursor: "pointer",
                                padding: "0 6px",
                                fontFamily: "inherit",
                            }}
                        >
                            Clear all
                        </button>
                    )}
                </div>
            )}
        </section>
    );
}
